import { AuthContext } from '@context/AuthContext';
import { FarmContext } from '@context/FarmContext';
import { registerToastErrorAction } from '@services/toast';
import { Suspense, lazy, useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { resolveErrorHelpPiece } from '../model/errorHelp';
import { helpStore, useHelpState } from './helpStore';

const HelpDrawer = lazy(async () => await import('./HelpDrawer'));
const GuidedTour = lazy(async () => await import('./GuidedTour'));

/**
 * Montado uma vez no layout autenticado. Mantém o escopo (usuário + fazenda),
 * liga erros conhecidos à orientação e só carrega gaveta e tour quando usados —
 * as telas não pagam o peso do catálogo.
 */
export function HelpRuntime() {
  const { user } = useContext(AuthContext);
  const { farm } = useContext(FarmContext);
  const { pathname } = useLocation();
  const drawerOpen = useHelpState((state) => state.drawerOpen);
  const tourId = useHelpState((state) => state.tourId);

  useEffect(() => {
    helpStore.setScope(`${user?.id ?? ''}:${farm ?? ''}:${pathname}`);
  }, [user?.id, farm, pathname]);

  useEffect(() => () => { helpStore.setScope(''); }, []);

  useEffect(
    () =>
      registerToastErrorAction((error, context, message) => {
        const pieceId = resolveErrorHelpPiece(error, context);
        if (!pieceId) return undefined;
        const { contextVersion } = helpStore.getState();
        helpStore.recordError({ pieceId, message });
        return {
          label: 'Como resolver',
          onSelect: () => {
            if (helpStore.getState().contextVersion !== contextVersion) return;
            helpStore.openPiece(pieceId);
          },
        };
      }),
    [],
  );

  // Ao fechar a gaveta, o foco volta ao botão que a abriu.
  const active = drawerOpen || Boolean(tourId);
  const wasOpen = useRef(active);
  useEffect(() => {
    if (wasOpen.current && !active) helpStore.restoreFocus();
    wasOpen.current = active;
  }, [active]);

  return (
    <Suspense fallback={null}>
      {drawerOpen ? <HelpDrawer /> : null}
      {tourId ? <GuidedTour /> : null}
    </Suspense>
  );
}
